import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { api } from '@/lib/api';
import type * as API from '@/types/api';
import { useAccountStore, useSubscriptionStore } from '@nimbitlabs/features';

interface TeamsState {
  teams: API.TeamDto[];
  loading: boolean;
  error: string | null;
  
  fetchTeams: () => Promise<void>;
  addTeam: (teamData: API.CreateTeamDto) => Promise<API.TeamDto>;
  updateTeam: (id: string, teamData: API.UpdateTeamDto) => Promise<void>;
  deleteTeam: (teamId: string) => Promise<void>;
  getTeamById: (id: string) => Promise<API.TeamDto>;
  addMember: (teamId: string, userId: string, role: string) => Promise<void>;
  removeMember: (teamId: string, userId: string) => Promise<void>;
  hasReachedLimit: () => boolean;
  getTeamLimit: () => { current: number; limit: number | null; hasReachedLimit: boolean };
  clearError: () => void;
}

const teamsStore = create<TeamsState>()(
  subscribeWithSelector((set, get) => ({
  teams: [],
  loading: false,
  error: null,
  
  fetchTeams: async () => {
    set({ loading: true, error: null });
    try {
      const data = await api.teams.getAll();
      set({ teams: data, loading: false });
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to fetch teams', loading: false });
    }
  },
  
  addTeam: async (teamData: API.CreateTeamDto) => {
    const tempId = `temp-${Date.now()}`;
    const optimisticTeam: API.TeamDto = {
      id: tempId,
      name: teamData.name,
      description: teamData.description || '',
      lead: teamData.lead || '',
      email: teamData.email || '',
      tags: teamData.tags || [],
      projects: teamData.projects || [],
      links: teamData.links || [],
      members: teamData.members || [],
      servicesCount: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isLocked: false,
    };

    set(state => ({ teams: [optimisticTeam, ...state.teams] }));

    try {
      const created = await api.teams.create(teamData);
      set(state => ({
        teams: state.teams.map(t => t.id === tempId ? created : t)
      }));
      return created;
    } catch (error) {
      set(state => ({
        teams: state.teams.filter(t => t.id !== tempId),
        error: error instanceof Error ? error.message : 'Failed to create team'
      }));
      throw error;
    }
  },
  
  updateTeam: async (id: string, teamData: API.UpdateTeamDto) => {
    const original = get().teams.find(t => t.id === id);
    
    set(state => ({
      teams: state.teams.map(t => 
        t.id === id ? { ...t, ...teamData, updatedAt: new Date().toISOString() } : t
      )
    }));
    
    try {
      const updated = await api.teams.update(id, teamData);
      set(state => ({
        teams: state.teams.map(t => t.id === id ? updated : t)
      }));
    } catch (error) {
      if (original) {
        set(state => ({
          teams: state.teams.map(t => t.id === id ? original : t)
        }));
      }
      throw error;
    }
  },
  
  deleteTeam: async (teamId: string) => {
    const original = get().teams;
    
    set(state => ({
      teams: state.teams.filter(t => t.id !== teamId)
    }));
    
    try {
      await api.teams.delete(teamId);
    } catch (error) {
      set({ teams: original });
      throw error;
    }
  },
  
  getTeamById: async (id: string) => {
    const existing = get().teams.find(t => t.id === id);
    if (existing) return existing;
    
    const data = await api.teams.getById(id);
    set(state => ({
      teams: [...state.teams.filter(t => t.id !== id), data]
    }));
    return data;
  },
  
  addMember: async (teamId: string, userId: string, role: string) => {
    try {
      await api.teams.addMember(teamId, userId, role);
      // Refresh the team to get updated members list
      const updated = await api.teams.getById(teamId);
      set(state => ({
        teams: state.teams.map(t => t.id === teamId ? updated : t)
      }));
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to add team member' });
      throw error;
    }
  },
  
  removeMember: async (teamId: string, userId: string) => {
    try {
      await api.teams.removeMember(teamId, userId);
      // Refresh the team to get updated members list
      const updated = await api.teams.getById(teamId);
      set(state => ({
        teams: state.teams.map(t => t.id === teamId ? updated : t)
      }));
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to remove team member' });
      throw error;
    }
  },
  
  hasReachedLimit: () => {
    const { limits } = useSubscriptionStore.getState();
    if (!limits) return true; // No data = assume limit reached
    const teamLimit = limits.features?.teams?.limit;
    if (teamLimit === undefined) return false; // No limit = never reached
    // Use actual count from store, not backend usage
    const currentCount = get().teams.length;
    return currentCount >= teamLimit;
  },
  
  getTeamLimit: () => {
    const { limits } = useSubscriptionStore.getState();
    // Use actual count from store, not backend usage
    const current = get().teams.length;
    const limit = limits?.features?.teams?.limit ?? null;
    const hasReachedLimit = limit !== null && current >= limit;
    return { current, limit, hasReachedLimit };
  },
  
  clearError: () => set({ error: null }),
  }))
);

export const useTeamsStore = teamsStore;

// Subscribe to account changes and auto-fetch
useAccountStore.subscribe(
  (state) => state.currentAccount,
  (currentAccount) => {
    if (currentAccount?.id) {
      teamsStore.getState().fetchTeams();
    } else {
      teamsStore.setState({ teams: [] });
    }
  }
);
